/** Who keeps an alias, in words (plan backend `2026-10-06-managed-ext-ids.md`): an import
 *  (`managed: true` — updated on every sync, read-only to ops until taken over) or ops
 *  (`managed: false` — no import changes or removes it). One vocabulary for the ext-ID
 *  grid, its drawer and the tour editor's identifiers table.
 *
 *  An import that names no restech (a direct XML import) owns every managed ID of the
 *  tour, so handing back an ID with no integration gives it to the next XML import. */
import { managedSource } from './extIdFormat';

export const TAKE_OVER_LABEL = 'Take over…';
export const HAND_BACK_LABEL = 'Hand back to import…';

/** Why a managed row's edit / delete is unavailable (tooltips). */
export const MANAGED_EDIT_REASON = 'Maintained by an import — take it over to edit it.';
export const MANAGED_DELETE_REASON =
  'Maintained by an import — take it over first, or its next sync recreates it.';

/** What taking an ID over changes. */
export const TAKE_OVER_EFFECT =
  'the import stops updating it, and future syncs leave it alone — they never change or remove it';

/** What handing an ID back sets off. `subject` names the ID ("“GYG-1”", "this ID"). */
export function handBackEffect(
  integration: string | null | undefined,
  account: string | null | undefined,
  subject = 'this ID',
): string {
  if (!integration) {
    return `The next XML import will maintain ${subject}: it overwrites manual edits, and deletes the ID if the import no longer lists it`;
  }
  return `From its next sync, the ${managedSource(integration, account)} import maintains ${subject}: it overwrites manual edits, and deletes the ID if the feed no longer lists it`;
}

/** Which ownership action a row offers: aliases only — a primary ID is the entity's own
 *  column, which no import's `<ext_ids>` manages. */
export function ownershipActionOf(row: {
  kind: 'primary' | 'alias';
  managed: boolean;
}): 'take_over' | 'hand_back' | null {
  if (row.kind !== 'alias') return null;
  return row.managed ? 'take_over' : 'hand_back';
}
